import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

export default function TermsPage() {
  const { t } = useTranslation();
  const terms = t('terms', { returnObjects: true }) as any;
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!terms || typeof terms === 'string') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center text-gray-500">
          Terms not available in this language
        </div>
      </div>
    );
  }

  const sections = terms.sections || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {terms.title || 'Terms & Conditions'}
          </h1>
          <p className="text-sm text-gray-500">
            Last updated: {terms.lastUpdated || '19 June 2026'}
          </p>
        </div>

        {/* Intro */}
        {terms.intro && (
          <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-gray-700">{terms.intro}</p>
          </div>
        )}

        {/* Sections */}
        <div className="space-y-4">
          {Object.entries(sections).map(([key, section]: [string, any]) => (
            <div
              key={key}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Section Header (Collapsible) */}
              <button
                onClick={() => toggleSection(key)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <h2 className="text-lg font-semibold text-gray-900 text-left">
                  {section.title}
                </h2>
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedSections[key] ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Section Content */}
              {expandedSections[key] && (
                <div className="px-4 pb-4 border-t border-gray-200 space-y-3">
                  {typeof section.content === 'object' && section.content !== null ? (
                    Object.entries(section.content).map(([contentKey, value]: [string, any]) => (
                      <div key={contentKey} className="text-gray-700 leading-relaxed">
                        {typeof value === 'string' && value ? (
                          <p>{value}</p>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-700">{section.content}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Info */}
        {sections.contactInfo && (
          <div className="mt-12 p-6 bg-gray-100 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {sections.contactInfo.title}
            </h3>
            {typeof sections.contactInfo.content === 'object' && (
              <div className="space-y-2 text-sm text-gray-700">
                {Object.entries(sections.contactInfo.content).map(
                  ([key, value]: [string, any]) => (
                    value && typeof value === 'string' && (
                      <div key={key}>
                        <strong className="capitalize">{key}:</strong> {value}
                      </div>
                    )
                  )
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
